/**
 * What to tell a customer whose photo took too long to read, and what to tell
 * Sentry about why.
 *
 * The timeout shipped assuming every stall was iCloud ("Optimise iPhone
 * Storage" keeps the photo in the cloud, so reading its bytes downloads it
 * first). The first real event proved that wrong: a Samsung Galaxy J4+ on
 * Android 8.1, in the Facebook in-app browser, reading a 4.2 MB photo
 * (FOTO-NEXTJS-16H). That customer was shown "open Settings → Photos and
 * choose Download and Keep Originals" — a menu their phone does not have.
 *
 * A slow read has several causes: a cloud-backed library on iOS, but also an
 * old phone, a heavy in-app WebView, or simply a large file. Only the iOS one
 * has a remedy the customer can act on, so only iOS is told about it.
 */

/** True for iPhone/iPad/iPod. Deliberately NOT true for macOS. */
function isIOS(userAgent: string): boolean {
  return /\b(iPhone|iPad|iPod)\b/.test(userAgent);
}

function agentOf(userAgent?: string): string {
  if (typeof userAgent === "string") return userAgent;
  return typeof navigator === "undefined" ? "" : navigator.userAgent;
}

/**
 * The translation key for the "these photos are slow" notice.
 *
 * iOS gets the iCloud remedy because it is actionable there; everyone else
 * gets a neutral message, because naming iCloud to an Android user is both
 * wrong and impossible to act on.
 */
export function stallAdviceKey(userAgent?: string): string {
  return isIOS(agentOf(userAgent))
    ? "order.noteStalledInCloud"
    : "order.noteStalledSlowRead";
}

/**
 * The `likelyCause` tag on the Sentry event.
 *
 * Kept honest: on iOS a stall really is usually an undownloaded iCloud photo,
 * but anywhere else that label would send whoever triages it hunting for a
 * cause that cannot exist on the device.
 */
export function stallCauseTag(userAgent?: string): string {
  return isIOS(agentOf(userAgent))
    ? "icloud-not-downloaded"
    : "slow-file-read";
}
